import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Share2, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Tiny CSS-only confetti burst. 24 pieces, no dependencies, mounts once.
 */
const ConfettiBurst = () => {
  const pieces = Array.from({ length: 24 });
  const colors = ['#F59E0B', '#FBBF24', '#EF4444', '#8B5CF6', '#10B981', '#EC4899'];
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 300;
        const duration = 1400 + Math.random() * 800;
        const rot = Math.random() * 360;
        const color = colors[i % colors.length];
        return (
          <span
            key={i}
            style={{
              position: 'absolute',
              left: `${left}%`,
              top: '-8%',
              width: 10,
              height: 14,
              background: color,
              transform: `rotate(${rot}deg)`,
              animation: `kindness-confetti-fall ${duration}ms ease-in ${delay}ms forwards`,
              borderRadius: 2,
            }}
          />
        );
      })}
      <style>{`
        @keyframes kindness-confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(700px) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

/**
 * Full-screen celebration modal. Shows when the parent detects a newly-earned
 * badge and passes it in.
 */
export const KindnessBadgeCelebration = ({ badge, onClose, shareUrl }) => {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!badge) return;
    closeBtnRef.current?.focus?.();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [badge, onClose]);

  if (!badge) return null;

  const handleShare = async () => {
    const text = `I just earned "${badge.title}" ${badge.emoji} on KindnessProject.app — commit a random act of kindness and follow it around the world.`;
    const url = shareUrl || 'https://kindnessproject.app';
    if (navigator.share) {
      try {
        await navigator.share({ title: badge.title, text, url });
        return;
      } catch (_) { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success('Shareable message copied to your clipboard');
    } catch (_) {
      toast.error('Unable to share');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="kindness-celebration-title"
      data-testid="kindness-celebration-dialog"
    >
      <div
        className="relative w-full max-w-md bg-gradient-to-b from-amber-50 to-white rounded-3xl shadow-2xl border-4 border-amber-400 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <ConfettiBurst />
        <button
          ref={closeBtnRef}
          onClick={onClose}
          className="absolute top-3 right-3 text-amber-800/70 hover:text-amber-950 z-10"
          aria-label="Close"
          data-testid="kindness-celebration-close"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="relative px-8 py-10 text-center">
          <div className="text-6xl mb-3 drop-shadow-lg" aria-hidden="true">{badge.emoji}</div>
          <div className="uppercase tracking-widest text-amber-700 text-xs font-bold mb-1">Badge Unlocked</div>
          <h2 id="kindness-celebration-title" className="text-2xl font-bold text-amber-950 mb-2">
            {badge.title}
          </h2>
          <p className="text-amber-900/80 text-sm leading-relaxed mb-6">{badge.description}</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={handleShare}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 font-bold"
              data-testid="kindness-celebration-share"
            >
              <Share2 className="w-4 h-4 mr-2" /> Share this badge
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="border-amber-400 text-amber-800 hover:bg-amber-100"
            >
              Keep spreading kindness
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KindnessBadgeCelebration;
